"use client"

import { useState } from "react"
import { Download, Loader2 } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { downloadMaeAnalyticsExcel } from "@/lib/agent-force"
import type { MaeAnalyticsPayload } from "@/lib/agent-force"

function fmtNum(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return "—"
  return v.toFixed(digits)
}

type Props = {
  data: MaeAnalyticsPayload | null
  loading?: boolean
}

export function MaeAnalytics({ data, loading }: Props) {
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const summary = data?.summary
  const trades = data?.trades ?? []
  const bySession = data?.by_session ?? []
  const sample = data?.sample_size ?? 0
  const withMae = data?.with_mae_data ?? 0

  const summaryCells = [
    { label: "Avg MAE", value: `${fmtNum(summary?.avg_mae_pips)} pips` },
    { label: "Avg SL", value: `${fmtNum(summary?.avg_sl_pips)} pips` },
    { label: "Avg MFE", value: `${fmtNum(summary?.avg_mfe_pips)} pips` },
    {
      label: "Avg Entry Eff",
      value: summary?.avg_entry_efficiency_r != null ? `${fmtNum(summary.avg_entry_efficiency_r, 2)}R` : "—",
    },
    {
      label: "MAE ≥ SL",
      value: `${fmtNum(summary?.pct_mae_exceeded_sl, 0)}%`,
      warn: (summary?.pct_mae_exceeded_sl ?? 0) > 40,
    },
    { label: "Win rate", value: `${fmtNum((summary?.win_rate ?? 0) * 100, 0)}%` },
    { label: "Tracked", value: `${withMae} / ${sample}` },
  ]

  return (
    <Card className="min-w-0">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base font-semibold">MAE Analytics</CardTitle>
            <p className="text-xs text-muted-foreground">
              Maximum Adverse Excursion — how far price moved against you before the outcome.
              Entry efficiency = MFE ÷ SL (R opportunity before outcome).
            </p>
          </div>
          {sample > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={exporting || loading}
              onClick={async () => {
                setExportError(null)
                setExporting(true)
                try {
                  await downloadMaeAnalyticsExcel()
                } catch (err) {
                  setExportError(err instanceof Error ? err.message : "Export failed")
                } finally {
                  setExporting(false)
                }
              }}
            >
              {exporting ? <Loader2 className="animate-spin" /> : <Download />}
              {exporting ? "Exporting…" : "Download Excel"}
            </Button>
          ) : null}
        </div>
        {exportError ? (
          <p className="mt-2 text-xs text-destructive">{exportError}</p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data ? (
          <p className="text-sm text-muted-foreground">Loading excursion data…</p>
        ) : sample === 0 ? (
          <p className="text-sm text-muted-foreground">
            No closed trades yet. MAE is recorded while positions are open and finalized on close.
          </p>
        ) : (
          <>
            <SummaryGrid cells={summaryCells} />

            {summary?.insight ? (
              <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100">
                {summary.insight}
              </p>
            ) : null}

            {bySession.length > 0 ? (
              <DataTable
                title="By session"
                headers={["Session", "Trades", "Win %", "Avg MAE", "Avg SL", "Avg MAE (R)"]}
                rows={bySession.map((r) => [
                  r.session,
                  String(r.trades),
                  `${fmtNum((r.win_rate ?? 0) * 100, 0)}%`,
                  fmtNum(r.avg_mae_pips),
                  fmtNum(r.avg_sl_pips),
                  fmtNum(r.avg_mae_r, 2),
                ])}
              />
            ) : null}

            {trades.length > 0 ? (
              <DataTable
                title={`Recent closed trades (${Math.min(12, trades.length)} of ${sample})`}
                headers={["Pair", "Dir", "MAE", "SL", "Δ", "MFE", "Eff (R)", "Entry", "Outcome", "Session"]}
                rows={trades.slice(0, 12).map((t) => [
                  String(t.symbol || "").replace(/\.m$/i, ""),
                  String(t.direction || "").toUpperCase(),
                  `${fmtNum(t.mae_pips)} p`,
                  `${fmtNum(t.sl_pips)} p`,
                  t.mae_vs_sl != null ? `${t.mae_vs_sl >= 0 ? "+" : ""}${fmtNum(t.mae_vs_sl)}` : "—",
                  `${fmtNum(t.mfe_pips)} p`,
                  t.entry_efficiency_r != null ? `${fmtNum(t.entry_efficiency_r, 2)}R` : "—",
                  t.entry_efficiency_label ?? "—",
                  t.outcome ?? "—",
                  t.fx_session ?? t.session_label ?? "—",
                ])}
              />
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function SummaryGrid({ cells }: { cells: { label: string; value: string; warn?: boolean }[] }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      {cells.map((c) => (
        <div key={c.label} className="rounded-lg border border-border bg-muted/30 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{c.label}</div>
          <div className={`text-sm font-semibold tabular-nums ${c.warn ? "text-amber-600 dark:text-amber-400" : ""}`}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  )
}

function DataTable({
  title,
  headers,
  rows,
}: {
  title: string
  headers: string[]
  rows: string[][]
}) {
  return (
    <div className="min-w-0 overflow-x-auto">
      <p className="mb-2 text-xs font-medium text-muted-foreground">{title}</p>
      <table className="w-full min-w-[680px] border-collapse text-xs">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            {headers.map((h) => (
              <th key={h} className="px-2 py-1.5 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-border/50 hover:bg-muted/40">
              {row.map((cell, j) => (
                <td key={j} className="px-2 py-1.5 tabular-nums">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
